import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNotifications, type Notification } from './use-notifications';

/**
 * LOK-1023: Pushes toast + desktop notifications for new critical/warning events.
 *
 * Tracks previously seen notification IDs so toasts only fire once per event.
 * Desktop browser notifications require user permission (requested on first critical event).
 *
 * Wire once in AppLayout — it reads from the existing useNotifications() hook
 * which derives state from polling data (system health, exchange health).
 */
export function useNotificationPush() {
  const { notifications } = useNotifications();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const seenIds = useRef<Set<string>>(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    // Skip the first render to avoid toasting existing state on page load
    if (!initialized.current) {
      for (const n of notifications) {
        seenIds.current.add(n.id);
      }
      initialized.current = true;
      return;
    }

    for (const n of notifications) {
      if (seenIds.current.has(n.id)) continue;
      seenIds.current.add(n.id);

      const title = t(n.title, n.tParams);
      const message = t(n.message, n.tParams);

      // Fire toast based on severity
      if (n.severity === 'critical') {
        toast.error(title, {
          description: message,
          duration: 15_000,
          action: n.link
            ? { label: t('buttons.view'), onClick: () => navigate(n.link ?? '') }
            : undefined,
        });
      } else if (n.severity === 'warning') {
        toast.warning(title, {
          description: message,
          duration: 10_000,
          action: n.link
            ? { label: t('buttons.view'), onClick: () => navigate(n.link ?? '') }
            : undefined,
        });
      }

      // Desktop notification for critical events
      if (n.severity === 'critical') {
        sendDesktopNotification(title, message, n);
      }
    }

    // Clean up IDs for notifications that no longer exist (e.g., bot recovered)
    const currentIds = new Set(notifications.map((n) => n.id));
    for (const id of seenIds.current) {
      if (!currentIds.has(id)) {
        seenIds.current.delete(id);
      }
    }
  }, [notifications, t, navigate]);
}

function sendDesktopNotification(title: string, body: string, n: Notification) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (document.hasFocus()) return; // Don't push desktop notification when tab is focused

  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.svg',
      tag: n.id, // Prevents duplicate desktop notifications
    });
    if (n.link) {
      notification.onclick = () => {
        window.focus();
        window.location.hash = ''; // Clear any hash
        window.location.pathname = n.link ?? '';
      };
    }
  } else if (Notification.permission === 'default') {
    // Request permission on first critical event
    Notification.requestPermission();
  }
}
